import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Sparkles, Gift, Trophy, CheckCircle2, Clock } from "lucide-react";

const BADGES = {
  insider: { label: "SlipShare Insider", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  ambassador: { label: "Ambassador", color: "bg-blue-100 text-blue-800 border-blue-300" },
  founders_circle: { label: "Founder's Circle", color: "bg-amber-100 text-amber-900 border-amber-400" },
};

export default function ReferralsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referrals/my").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-muted-foreground">Loading…</p>;
  if (!data) return null;

  const shareUrl = `${window.location.origin}/register?ref=${data.referral_code}`;
  const copy = (val, label = "Copied") => { navigator.clipboard.writeText(val); toast.success(label); };

  return (
    <div data-testid="referrals-panel" className="space-y-10">
      {/* Balance + Code */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Credit balance</div>
          <div className="font-display text-4xl mt-2" data-testid="credit-balance">${data.credit_balance.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground mt-1">Auto-applied at checkout · expires 12 mo from earning</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Qualified referrals</div>
          <div className="font-display text-4xl mt-2" data-testid="qualified-count">{data.qualified_count}</div>
          <div className="text-xs text-muted-foreground mt-1">Referees who completed a paid booking</div>
        </div>
        <div className="rounded-2xl border border-primary/40 bg-primary/5 p-6">
          <div className="text-xs uppercase tracking-widest text-primary">Your referral code</div>
          <div className="font-mono text-2xl mt-2 tracking-wide" data-testid="referral-code">{data.referral_code}</div>
          <div className="flex gap-2 mt-3">
            <Button data-testid="copy-code-btn" variant="outline" size="sm" className="gap-1" onClick={() => copy(data.referral_code, "Code copied")}>
              <Copy className="h-3.5 w-3.5"/> Code
            </Button>
            <Button data-testid="copy-link-btn" variant="outline" size="sm" className="gap-1" onClick={() => copy(shareUrl, "Share link copied")}>
              <Copy className="h-3.5 w-3.5"/> Share link
            </Button>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="rounded-2xl border border-border bg-secondary/40 p-6">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-accent mt-1" strokeWidth={1.5}/>
          <div>
            <div className="font-display text-xl">How referrals work</div>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Share your code with anyone who owns a boat or a dock. When they complete their first paid booking on SlipShare,
              <strong className="text-foreground"> you both earn 5% of that booking</strong> as credit. Hit a milestone below and you unlock a bonus check on top.
            </p>
          </div>
        </div>
      </div>

      {/* Milestones */}
      <div>
        <div className="font-display text-2xl mb-6 flex items-center gap-2"><Trophy className="h-5 w-5 text-accent" strokeWidth={1.5}/> Milestones</div>
        <div className="grid md:grid-cols-3 gap-4">
          {data.milestones.map((m) => {
            const pct = Math.min(100, Math.round((data.qualified_count / m.threshold) * 100));
            return (
              <div key={m.code} data-testid={`milestone-${m.code}`} className={`rounded-2xl border p-5 ${m.achieved ? "border-accent bg-accent/5" : "border-border bg-card"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">{m.threshold} referrals</div>
                    <div className="font-display text-xl mt-1">{m.label}</div>
                  </div>
                  {m.achieved ? (
                    <CheckCircle2 className="h-6 w-6 text-accent" strokeWidth={1.5}/>
                  ) : (
                    <Gift className="h-6 w-6 text-muted-foreground" strokeWidth={1.5}/>
                  )}
                </div>
                <div className="mt-3 text-2xl font-medium">${m.amount.toFixed(0)} <span className="text-sm text-muted-foreground font-normal">bonus credit</span></div>
                <Progress value={pct} className="mt-4 h-1.5"/>
                <div className="mt-2 text-xs text-muted-foreground">{Math.min(data.qualified_count, m.threshold)} / {m.threshold} {m.achieved ? "· Unlocked" : ""}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Referrals list */}
      <div>
        <div className="font-display text-2xl mb-4">Your referrals</div>
        {data.referrals.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground">
            No referrals yet. Share your code above to get started.
          </div>
        ) : (
          <div className="space-y-2">
            {data.referrals.map((r) => (
              <div key={r.referral_id} data-testid={`ref-row-${r.referral_id}`} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{r.referee_name}</div>
                  <div className="text-xs text-muted-foreground">Signed up {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-3">
                  {r.status === "qualified" ? (
                    <>
                      <span className="text-sm text-accent font-medium">+${(r.reward_amount || 0).toFixed(2)}</span>
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">Qualified</Badge>
                    </>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-300 gap-1"><Clock className="h-3 w-3"/>Awaiting first booking</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Achieved badges */}
      {data.achieved_badges.length > 0 && (
        <div>
          <div className="font-display text-2xl mb-4">Your badges</div>
          <div className="flex flex-wrap gap-2">
            {data.achieved_badges.map((b) => (
              <span key={b} data-testid={`badge-${b}`} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${BADGES[b]?.color || "bg-secondary"}`}>
                <Trophy className="h-3 w-3"/> {BADGES[b]?.label || b}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
